/**
 * IndexedDB store for survey drafts, queued photos, pending transcripts.
 *
 * Follows the existing OfflineStore pattern (idb library, singleton export)
 * but with survey-specific object stores for findings, photos, transcripts,
 * and documents.
 */

import { openDB, type IDBPDatabase } from 'idb'
import type {
  SurveySession,
  FacilityArea,
  SurveyDomain,
  SeverityLevel,
} from '../services/survey'
import { encodeBlobForStorage, PHOTO_STORAGE_READ_FAILED_MESSAGE } from './photoPreview'

// ── DB schema ───────────────────────────────────────────────────────────────

const DB_NAME = 'asc-survey-offline'
const DB_VERSION = 3

const STORES = {
  sessions: 'survey-sessions',
  findings: 'survey-findings',
  photos: 'survey-photos',
  transcripts: 'survey-transcripts',
  documents: 'survey-documents',
  syncQueue: 'survey-sync-queue',
  walkthroughState: 'survey-walkthrough-state',
  evidencePreviews: 'survey-evidence-previews',
} as const

// ── Queue item types ────────────────────────────────────────────────────────

export type SyncAction =
  | 'upload_photo'
  | 'submit_finding'
  | 'submit_tracer_step'
  | 'submit_document'
  | 'submit_transcript'

export interface SyncQueueItem {
  id: string
  action: SyncAction
  payload: Record<string, unknown>
  visit_id: string
  survey_id: string
  created_at: string
  retry_count: number
  last_error: string | null
  /** True when the item has been classified as unrecoverable (e.g. lost blob). retryAll will not reset these. */
  terminal?: boolean
}

export interface PhotoPin {
  id: string
  /** Relative position (0-1) */
  x: number
  y: number
  comment: string
  severity: SeverityLevel
  created_at: string
}

export interface PendingPhoto {
  photo_id: string
  survey_id: string
  visit_id: string
  area: FacilityArea
  caption: string | null
  blob: Blob
  mime_type: string
  gps_lat: number | null
  gps_lng: number | null
  taken_at: string
  /** Pin annotations placed on the photo */
  annotations?: PhotoPin[]
}

export interface PendingTranscript {
  transcript_id: string
  survey_id: string
  visit_id: string
  area: FacilityArea | null
  text: string
  audio_blob: Blob | null
  source: 'web_speech' | 'whisper' | 'manual'
  created_at: string
}

export interface PendingFinding {
  finding_id: string
  survey_id: string
  visit_id: string
  domain: SurveyDomain
  standard_ref: string
  severity: SeverityLevel
  observation: string
  area: FacilityArea
  photo_ids: string[]
  audio_transcript: string | null
  created_at: string
}

export interface PendingDocument {
  document_id: string
  survey_id: string
  visit_id: string
  doc_type: 'policy' | 'log' | 'record' | 'certificate' | 'other'
  label: string
  blob: Blob | null
  mime_type: string | null
  area: FacilityArea | null
  created_at: string
}

export interface WalkthroughStepState {
  status: 'pending' | 'finding' | 'skipped' | 'clear'
  findingIds: string[]
  photoIds: string[]
  notes: string
  checklistResponses?: Record<string, 'yes' | 'no' | 'na'>
  promptEvidence?: Record<string, {
    note: string
    photoIds: string[]
    severity?: 1 | 2 | 3
    photoAnnotations?: Record<string, PhotoPin[]>
  }>
}

export interface WalkthroughState {
  survey_id: string
  areaIdx: number
  sectionIdx: number
  itemIdx: number
  areaPhase?: 'briefing' | 'photos' | 'inspection'
  currentRoom?: string | null
  stepStates: Record<string, WalkthroughStepState>
  lastUpdated: string
}

export interface CachedEvidencePreview {
  cache_key: string
  visit_id: string
  evidence_id: string
  data_url: string
  cached_at: string
}

// ── Store class ─────────────────────────────────────────────────────────────

class SurveyOfflineStore {
  private db: Promise<IDBPDatabase> | null

  constructor() {
    if (typeof indexedDB === 'undefined') {
      this.db = null
      return
    }
    this.db = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORES.sessions)) {
          db.createObjectStore(STORES.sessions, { keyPath: 'survey_id' })
        }
        if (!db.objectStoreNames.contains(STORES.findings)) {
          db.createObjectStore(STORES.findings, { keyPath: 'finding_id' })
        }
        if (!db.objectStoreNames.contains(STORES.photos)) {
          db.createObjectStore(STORES.photos, { keyPath: 'photo_id' })
        }
        if (!db.objectStoreNames.contains(STORES.transcripts)) {
          db.createObjectStore(STORES.transcripts, { keyPath: 'transcript_id' })
        }
        if (!db.objectStoreNames.contains(STORES.documents)) {
          db.createObjectStore(STORES.documents, { keyPath: 'document_id' })
        }
        if (!db.objectStoreNames.contains(STORES.syncQueue)) {
          db.createObjectStore(STORES.syncQueue, { keyPath: 'id' })
        }
        // v2: walkthrough position + step state persistence
        if (!db.objectStoreNames.contains(STORES.walkthroughState)) {
          db.createObjectStore(STORES.walkthroughState, { keyPath: 'survey_id' })
        }
        if (!db.objectStoreNames.contains(STORES.evidencePreviews)) {
          db.createObjectStore(STORES.evidencePreviews, { keyPath: 'cache_key' })
        }
      },
    })
  }

  private async getDb(): Promise<IDBPDatabase> {
    if (!this.db) throw new Error('IndexedDB not available')
    return this.db
  }

  // ── Session drafts ──────────────────────────────────────────────────────

  async saveSession(session: SurveySession): Promise<void> {
    const db = await this.getDb()
    await db.put(STORES.sessions, session)
  }

  async getSession(surveyId: string): Promise<SurveySession | undefined> {
    const db = await this.getDb()
    return db.get(STORES.sessions, surveyId)
  }

  async getSessionByVisit(visitId: string): Promise<SurveySession | undefined> {
    const db = await this.getDb()
    const all = await db.getAll(STORES.sessions)
    return all.find((s) => s.visit_id === visitId)
  }

  async removeSession(surveyId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.sessions, surveyId)
  }

  // ── Findings ────────────────────────────────────────────────────────────

  async saveFinding(finding: PendingFinding): Promise<void> {
    const db = await this.getDb()
    await db.put(STORES.findings, finding)
  }

  async getFinding(findingId: string): Promise<PendingFinding | undefined> {
    const db = await this.getDb()
    return db.get(STORES.findings, findingId)
  }

  async getFindingsBySurvey(surveyId: string): Promise<PendingFinding[]> {
    const db = await this.getDb()
    const all: PendingFinding[] = await db.getAll(STORES.findings)
    return all.filter((f) => f.survey_id === surveyId)
  }

  async removeFinding(findingId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.findings, findingId)
  }

  // ── Photos ──────────────────────────────────────────────────────────────

  /**
   * Persist a photo for offline use.
   *
   * WebKit/Firefox IndexedDB cannot reliably store Blob/File objects, so we
   * store a base64 data URL. Prefer a precomputed `dataUrl` from the capture
   * path (PhotoCapture materializes immediately so File-backed blobs cannot
   * go unreadable after the picker closes — HCP#1664). When omitted, encode
   * via multi-strategy `encodeBlobForStorage` (arrayBuffer → FileReader+timeout
   * → objectURL fetch). Empty / unreadable payloads fail closed.
   */
  async savePhoto(photo: PendingPhoto, opts?: { dataUrl?: string }): Promise<void> {
    const db = await this.getDb()

    let dataUrl = opts?.dataUrl
    if (
      typeof dataUrl !== 'string' ||
      !dataUrl.startsWith('data:') ||
      !dataUrl.includes(',') ||
      dataUrl.length <= 'data:,'.length
    ) {
      dataUrl = await encodeBlobForStorage(photo.blob, photo.mime_type)
    }
    if (!dataUrl || dataUrl.length <= 'data:,'.length) {
      console.error('[surveyStore] Could not read photo blob for local storage')
      throw new Error(PHOTO_STORAGE_READ_FAILED_MESSAGE)
    }

    const record: Record<string, unknown> = {
      photo_id: photo.photo_id,
      survey_id: photo.survey_id,
      visit_id: photo.visit_id,
      area: photo.area,
      caption: photo.caption,
      mime_type: photo.mime_type,
      gps_lat: photo.gps_lat,
      gps_lng: photo.gps_lng,
      taken_at: photo.taken_at,
      annotations: photo.annotations,
      _blobDataUrl: dataUrl,
    }
    await db.put(STORES.photos, record)
  }

  async getPhoto(photoId: string): Promise<PendingPhoto | undefined> {
    const db = await this.getDb()
    const record = await db.get(STORES.photos, photoId) as any
    if (!record) return undefined
    this.hydrateBlob(record)
    return record as PendingPhoto
  }

  async getPhotosBySurvey(surveyId: string): Promise<PendingPhoto[]> {
    const db = await this.getDb()
    const all: any[] = await db.getAll(STORES.photos)
    return all
      .filter((p) => p.survey_id === surveyId)
      .map((record) => {
        this.hydrateBlob(record)
        return record as PendingPhoto
      })
  }

  /** Reconstruct blob from stored data URL or ArrayBuffer (backwards compat). */
  private hydrateBlob(record: any): void {
    if (record._blobDataUrl) {
      // Convert data URL to Blob
      const [header, b64] = record._blobDataUrl.split(',')
      const mime = header.match(/:(.*?);/)?.[1] || record.mime_type
      const bytes = atob(b64)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
      record.blob = new Blob([arr], { type: mime })
      delete record._blobDataUrl
    } else if (record._blobBuffer) {
      // Legacy: ArrayBuffer storage
      record.blob = new Blob([record._blobBuffer], { type: record._blobType || record.mime_type })
      delete record._blobBuffer
      delete record._blobType
    }
  }

  async removePhoto(photoId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.photos, photoId)
  }

  // ── Transcripts ─────────────────────────────────────────────────────────

  async saveTranscript(transcript: PendingTranscript): Promise<void> {
    const db = await this.getDb()
    await db.put(STORES.transcripts, transcript)
  }

  async getTranscript(transcriptId: string): Promise<PendingTranscript | undefined> {
    const db = await this.getDb()
    return db.get(STORES.transcripts, transcriptId)
  }

  async getTranscriptsBySurvey(surveyId: string): Promise<PendingTranscript[]> {
    const db = await this.getDb()
    const all: PendingTranscript[] = await db.getAll(STORES.transcripts)
    return all.filter((t) => t.survey_id === surveyId)
  }

  async removeTranscript(transcriptId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.transcripts, transcriptId)
  }

  // ── Documents ───────────────────────────────────────────────────────────

  async saveDocument(doc: PendingDocument): Promise<void> {
    const db = await this.getDb()
    await db.put(STORES.documents, doc)
  }

  async getDocument(documentId: string): Promise<PendingDocument | undefined> {
    const db = await this.getDb()
    return db.get(STORES.documents, documentId)
  }

  async getDocumentsBySurvey(surveyId: string): Promise<PendingDocument[]> {
    const db = await this.getDb()
    const all: PendingDocument[] = await db.getAll(STORES.documents)
    return all.filter((d) => d.survey_id === surveyId)
  }

  async removeDocument(documentId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.documents, documentId)
  }

  // ── Walkthrough state ──────────────────────────────────────────────

  async saveWalkthroughState(state: WalkthroughState): Promise<void> {
    const db = await this.getDb()
    await db.put(STORES.walkthroughState, { ...state, lastUpdated: new Date().toISOString() })
  }

  async getWalkthroughState(surveyId: string): Promise<WalkthroughState | undefined> {
    const db = await this.getDb()
    return db.get(STORES.walkthroughState, surveyId)
  }

  async removeWalkthroughState(surveyId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.walkthroughState, surveyId)
  }

  // ── Report evidence preview cache ──────────────────────────────────

  async saveEvidencePreview(preview: Omit<CachedEvidencePreview, 'cache_key' | 'cached_at'>): Promise<void> {
    const db = await this.getDb()
    const cacheKey = `${preview.visit_id}:${preview.evidence_id}`
    await db.put(STORES.evidencePreviews, {
      ...preview,
      cache_key: cacheKey,
      cached_at: new Date().toISOString(),
    })
  }

  async getEvidencePreview(visitId: string, evidenceId: string): Promise<CachedEvidencePreview | undefined> {
    const db = await this.getDb()
    return db.get(STORES.evidencePreviews, `${visitId}:${evidenceId}`)
  }

  // ── Sync queue ──────────────────────────────────────────────────────────

  async enqueue(item: SyncQueueItem): Promise<void> {
    const db = await this.getDb()
    await db.put(STORES.syncQueue, item)
  }

  async dequeue(id: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORES.syncQueue, id)
  }

  async updateQueueItem(id: string, patch: Partial<SyncQueueItem>): Promise<void> {
    const db = await this.getDb()
    const existing = await db.get(STORES.syncQueue, id)
    if (!existing) return
    await db.put(STORES.syncQueue, { ...existing, ...patch })
  }

  async getAllQueued(): Promise<SyncQueueItem[]> {
    const db = await this.getDb()
    return db.getAll(STORES.syncQueue)
  }

  async getQueuedBySurvey(surveyId: string): Promise<SyncQueueItem[]> {
    const db = await this.getDb()
    const all: SyncQueueItem[] = await db.getAll(STORES.syncQueue)
    return all.filter((q) => q.survey_id === surveyId)
  }

  async getQueueLength(): Promise<number> {
    const db = await this.getDb()
    const all = await db.getAll(STORES.syncQueue)
    return all.length
  }

  // ── Bulk cleanup ────────────────────────────────────────────────────────

  async wipeSurvey(surveyId: string): Promise<void> {
    const db = await this.getDb()
    const allStores = [STORES.sessions, STORES.findings, STORES.photos, STORES.transcripts, STORES.documents, STORES.syncQueue, STORES.walkthroughState, STORES.evidencePreviews] as const
    const tx = db.transaction([...allStores], 'readwrite')

    const deleteMatching = async (storeName: (typeof allStores)[number], key: string) => {
      const store = tx.objectStore(storeName)
      const all = await store.getAll()
      for (const item of all) {
        if (item.survey_id === surveyId || item[key] === surveyId) {
          await store.delete(item[store.keyPath as string])
        }
      }
    }

    await Promise.all([
      tx.objectStore(STORES.sessions).delete(surveyId),
      deleteMatching(STORES.findings, 'survey_id'),
      deleteMatching(STORES.photos, 'survey_id'),
      deleteMatching(STORES.transcripts, 'survey_id'),
      deleteMatching(STORES.documents, 'survey_id'),
      deleteMatching(STORES.syncQueue, 'survey_id'),
      tx.objectStore(STORES.walkthroughState).delete(surveyId),
    ])

    await tx.done
  }

  async wipeAll(): Promise<void> {
    if (!this.db) return
    const db = await this.getDb()
    const tx = db.transaction(Object.values(STORES), 'readwrite')
    for (const storeName of Object.values(STORES)) {
      await tx.objectStore(storeName).clear()
    }
    await tx.done
  }

  /**
   * One-shot recovery: find queue items whose stored photo has an empty/missing
   * blob data URL (left over from the old silent Strategy 3 fallback) and mark
   * them terminal so retryAll() does not loop on them forever.
   *
   * Called once at module load; errors are swallowed so offline boot is never
   * blocked.
   */
  async purgeOrphanedPhotoQueue(): Promise<number> {
    const db = await this.getDb()
    const MAX_RETRIES_SENTINEL = 5

    // Read raw photo records to detect empty _blobDataUrl without hydration.
    const rawPhotos: Array<{ photo_id: string; _blobDataUrl?: string }> =
      await db.getAll(STORES.photos)
    const orphanPhotoIds = new Set(
      rawPhotos
        .filter((p) => !p._blobDataUrl)
        .map((p) => p.photo_id),
    )

    if (orphanPhotoIds.size === 0) return 0

    const queueItems: SyncQueueItem[] = await db.getAll(STORES.syncQueue)
    let marked = 0
    for (const item of queueItems) {
      if (
        item.action === 'upload_photo' &&
        typeof item.payload.photo_id === 'string' &&
        orphanPhotoIds.has(item.payload.photo_id) &&
        !item.terminal
      ) {
        await db.put(STORES.syncQueue, {
          ...item,
          terminal: true,
          retry_count: MAX_RETRIES_SENTINEL,
          last_error: 'Photo data lost — recapture required',
        })
        marked++
      }
    }
    return marked
  }
}

export const surveyStore = new SurveyOfflineStore()

// One-shot: silence any pre-existing orphaned photo queue items so they don't
// bounce in retryAll() loops after an upgrade from the old silent fallback.
;(async () => {
  try {
    await surveyStore.purgeOrphanedPhotoQueue()
  } catch {
    // Swallow — must not block app startup
  }
})()
